import type { Moneda } from "@/lib/informes";
import { numeroEscrito } from "@/lib/numeroEscrito";

/* ---------------------------------------------------------------------------
   Formato de los montos y las cifras que las pestañas técnicas del informe
   cargan a mano: maquinarias, instalaciones, construcciones.

   NACIÓ POR EXTRACCIÓN de `components/informes/maquinaria.ts` cuando entraron
   las otras dos pestañas. Eran setenta líneas que no sabían nada de
   maquinaria, y copiarlas dos veces habría dejado tres lugares donde decidir
   cómo se imprime un ajuste o un par de vida útil.

   NO HAY ARITMÉTICA DE VALORACIÓN acá, y es a propósito: los totales los
   calcula el SERVIDOR y estas pantallas solo los imprimen. No es el caso de
   `homologacion.ts`, que sí es gemelo de su calculador en PHP porque tiene que
   mover la cifra mientras alguien elige — acá ningún formulario previsualiza
   un total.
--------------------------------------------------------------------------- */

/**
 * Un monto con su moneda adelante, o "—".
 *
 * EL SÍMBOLO ACOMPAÑA A LA CIFRA SIEMPRE, y eso no es adorno: en la ficha de
 * un comparable se imprimió "$ 1.639" sobre un precio que estaba en UF —mil
 * seiscientos pesos por ochenta hectáreas—, y nada en la pantalla lo
 * contradecía.
 *
 * `moneda` EN `null` IMPRIME LA CIFRA DESNUDA, y sirve para las columnas de un
 * cuadro que ya declara su unidad en el encabezado: en maquinaria eso es la
 * columna "Moneda" de la propia fila, y en instalaciones y construcciones es
 * el "UF" del título de cada columna. Repetir el símbolo en cada celda de una
 * tabla que ya lo dice arriba es ruido.
 *
 * "—" y nunca "0": un valor sin cargar no es algo que no vale nada.
 */
export function formatearMonto(valor: number | null, moneda: Moneda | null): string {
  if (valor === null) return "—";

  const cifra = new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(valor);

  return moneda === null ? cifra : `${simboloDe(moneda)} ${cifra}`;
}

/** Cómo se escribe cada moneda delante de una cifra. Espejo de `Moneda::simbolo()`. */
export function simboloDe(moneda: Moneda): string {
  return moneda === "USD" ? "US$" : moneda === "CLP" ? "$" : "UF";
}

/**
 * Una cifra decimal que llega del backend como string, formateada a la
 * chilena, o "—".
 *
 * Es lo que usan las columnas de un cuadro que ya declara su unidad arriba —la
 * cantidad de una instalación, su valor en UF por unidad—: el `decimal:2` de
 * PHP llega como `"1000.00"` y hay que imprimirlo `1.000`.
 */
export function formatearCifra(valor: string | null): string {
  if (valor === null || valor.trim() === "") return "—";

  const numero = Number(valor);

  return Number.isFinite(numero) ? formatearMonto(numero, null) : "—";
}

/**
 * El ajuste comercial CON SU SIGNO, incluido el `+`.
 *
 * El signo se imprime siempre por lo mismo que en la homologación: casi todos
 * los ajustes descuentan, así que un "15 %" a secas se lee como un descuento
 * más y un aumento pasaría desapercibido.
 */
export function formatearAjuste(pct: string | null): string {
  if (pct === null || pct.trim() === "") return "—";

  const valor = Number(pct);
  if (!Number.isFinite(valor)) return "—";

  const cifra = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(valor);

  return `${valor > 0 ? "+" : ""}${cifra} %`;
}

/**
 * La vida útil total y la residual como las imprime el documento: "15 / 9".
 *
 * Cada mitad cae en "—" por separado: que falte la residual no borra la total,
 * y al revés. Con las dos vacías no se dibuja la barra, que sin números no
 * separa nada.
 */
export function formatearVidaUtil(total: number | null, residual: number | null): string {
  if (total === null && residual === null) return "—";

  return `${total ?? "—"} / ${residual ?? "—"}`;
}

/**
 * Cómo se verá guardado lo que se escribió en un campo de valor.
 *
 * La pantalla lo MUESTRA al lado del campo por el mismo motivo que el modal de
 * corrección de comparables: el punto de miles tiene dos lecturas —"45.000"
 * son cuarenta y cinco mil, pero quien copia de una pantalla en inglés escribe
 * "4.68" queriendo decir cuatro coma sesenta y ocho— y la ambigüedad se
 * resuelve a la vista de la persona, no a sus espaldas.
 *
 * `null` cuando el campo está vacío o no es un número, y ahí no se muestra
 * nada.
 */
export function comoSeGuardara(escrito: string, moneda: Moneda | null): string | null {
  const valor = numeroEscrito(escrito);

  return valor === null ? null : formatearMonto(valor, moneda);
}

/**
 * `null` → `""`, para que ningún `<input>` pase a no controlado.
 *
 * Un `<input value={null}>` se vuelve no controlado: React avisa por consola y
 * pierde lo que se escriba después.
 */
export function textoDeNumero(valor: number | null): string {
  return valor === null ? "" : String(valor);
}

/**
 * `"48000.00"` → `"48000"`, `"-6.50"` → `"-6,5"`.
 *
 * Saca el relleno de ceros del `decimal:2` de PHP y usa la coma decimal, que
 * es la que corresponde acá. NO ES COSMÉTICO: con el punto crudo, reabrir una
 * fila y guardarla sin tocar nada la releería como miles — "48000.00" pasaría
 * a valer 48.
 */
export function textoDeDecimal(valor: string | null): string {
  if (valor === null || valor.trim() === "") return "";

  const numero = Number(valor);
  if (!Number.isFinite(numero)) return valor;

  return numero.toString().replace(".", ",");
}
